import { generateId } from '@vedalib/editor/lib/utils/id'
import cn from 'classnames'
import type { MotionProps } from 'framer-motion'
import { motion } from 'framer-motion'
import type { CSSProperties, FC } from 'react'
import React, { useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'

import * as s from './DropDownContent.module.scss'
import EmptyContent from './EmptyContent'
import { useDropDownContext } from './context'
import type { IStyleType, ITheme, IType } from './types'

export type DropdownAnimationProps = Pick<MotionProps, 'initial' | 'animate' | 'exit'>

interface IDropDownContent extends DropdownAnimationProps {
  textError?: string
  styleType?: IStyleType
  styleContent: React.CSSProperties | undefined
  setPopupEle: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>
  open: boolean
  refPortal: React.MutableRefObject<HTMLDivElement | null>
  type?: IType
  theme?: ITheme
  overlayStyle?: CSSProperties
  popupContainer?: HTMLElement
  stopPropagation?: boolean
  children: React.ReactNode
  transformOrigin: React.CSSProperties['transformOrigin']
}

export const DropDownContent: FC<IDropDownContent> = ({
  textError,
  styleType,
  styleContent,
  setPopupEle,
  open,
  refPortal,
  children,
  type,
  theme,
  overlayStyle,
  popupContainer,
  stopPropagation,
  transformOrigin,
  ...rest
}) => {
  const { contextValue, parentContext } = useDropDownContext()
  const id = useMemo(() => generateId(), [])

  const setPopupRef = useCallback((node: HTMLDivElement) => {
    if (node instanceof HTMLElement) {
      setPopupEle(node)
      parentContext?.registerSubPopup(id, node)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const container = document.createElement('div')
    container.className = s.portalDropDown
    container.dataset.uikitPopupPortal = ''
    refPortal.current = container
    document.body.appendChild(container)
    return () => {
      document.body.removeChild(container)
      contextValue?.unRegisterSubPopup(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cnDefault = cn(s.root, styleType && s[styleType], type && s[type], theme && s[theme])

  const content = (
    <motion.div
      {...rest}
      className={cnDefault}
      data-uikit-popup-overlay=''
      onClick={(e) => stopPropagation && e.stopPropagation()}
      onMouseDown={(e) => stopPropagation && e.stopPropagation()}
      ref={setPopupRef}
      style={{ ...styleContent, ...overlayStyle, transformOrigin }}
      tabIndex={-1}
    >
      {children}
      {!children && type !== 'tooltip' && <EmptyContent textError={textError} />}
    </motion.div>
  )

  return open ? createPortal(content, popupContainer || refPortal.current || document.body) : null
}

export default DropDownContent
